// Ingest worker health indicator.
//
// Decision history:
//   Session 17 bug #2: badge hard-rendered "ingest offline" red on any error,
//     including from pages that don't actually need the ingest service (e.g.
//     paste-JSON mode). Misleading and alarming.
//   Session 19E (§4) fix:
//     - i18n labels (no hardcoded English)
//     - `mode` prop ('normal' | 'paste-json' | 'standalone')
//     - When mode !== 'normal', poll is disabled; badge shows neutral state
//     - Error state uses softer "degraded" styling, label "Generator not connected"
//       instead of red ERROR — the worker may be intentionally down on dev.

import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { checkHealth } from '../api/ingest.js';
import { useT } from '../i18n/index.js';

export type HealthBadgeMode = 'normal' | 'paste-json' | 'standalone';

export interface HealthBadgeProps {
  /**
   * normal      — actively poll /health, show provider/model when up.
   * paste-json  — paste-JSON path; worker not required; show neutral pill.
   * standalone  — page that doesn't talk to the worker (home, settings); hide.
   */
  mode?: HealthBadgeMode;
}

export function HealthBadge({ mode = 'normal' }: HealthBadgeProps) {
  const t = useT();
  const enabled = mode === 'normal';
  const { data, isError, isFetching } = useQuery({
    queryKey: ['health'],
    queryFn: checkHealth,
    staleTime: 30_000,
    retry: 1,
    enabled,
  });

  if (mode === 'standalone') return null;

  if (mode === 'paste-json') {
    return (
      <div className="health-badge health-badge-idle" title={t('health.pasteJsonMode')}>
        <Activity size={14} aria-hidden="true" />
        <span>{t('health.pasteJsonMode')}</span>
      </div>
    );
  }

  let label = t('health.checking');
  let cls = 'health-badge health-badge-loading';

  if (isFetching && !data) {
    label = t('health.checking');
  } else if (isError) {
    // Softer than "Offline" — the worker simply isn't reachable; user may
    // intentionally be running without it (dev / paste-JSON path).
    label = t('health.notConnected');
    cls = 'health-badge health-badge-degraded';
  } else if (data) {
    label = `${data.provider} · ${data.model}`;
    cls = 'health-badge health-badge-ok';
  }

  return (
    <div className={cls} title={label}>
      <Activity size={14} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
